import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API } from "@/lib/api";
import { useTenantPath } from "@/lib/tenantPath";
import { Download, FileText, Check, RotateCcw, Trash2, Filter, FileDown, AlertTriangle } from "lucide-react";

const STATUS_CLS = {
  pago: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pendente: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function Invoices() {
  const { t } = useTranslation("invoices");
  const { t: tc } = useTranslation("common");
  const tPath = useTenantPath();
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [statusFilter, setStatusFilter] = useState("");
  const [data, setData] = useState({ invoices: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("byPromoter"); // invoices | byPromoter
  const [promoterRows, setPromoterRows] = useState([]);

  const fetchData = () => {
    setLoading(true);
    const params = {};
    if (month) params.month = month;
    if (statusFilter) params.status_filter = statusFilter;
    api.get("/invoices", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [month, statusFilter]);

  useEffect(() => {
    if (view !== "byPromoter") return;
    const params = {};
    if (month) params.month = month;
    api.get("/finance/monthly-by-promoter", { params })
      .then((r) => setPromoterRows(r.data || []))
      .catch(() => setPromoterRows([]));
  }, [view, month]);

  const togglePaid = async (inv) => {
    if (inv.status === "pago") await api.post(`/invoices/${inv.id}/mark-pending`);
    else await api.post(`/invoices/${inv.id}/mark-paid`);
    fetchData();
  };

  const removeInv = async (inv) => {
    if (!window.confirm(t("actions.deleteConfirm", { number: inv.number }))) return;
    await api.delete(`/invoices/${inv.id}`);
    fetchData();
  };

  const exportCsv = () => {
    const token = localStorage.getItem("labora_token");
    const url = `${API}/invoices/export.csv?month=${encodeURIComponent(month || "")}${token ? "" : ""}`;
    // Use fetch with auth header to download (cookies + bearer)
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `recibos_${month || "todos"}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const downloadPdf = (inv) => {
    const token = localStorage.getItem("labora_token");
    fetch(`${API}/invoices/${inv.id}/pdf`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `recibo_${inv.number}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const totals = data.totals || {};
  const months = useMemo(() => {
    const arr = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
      arr.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
    }
    return arr;
  }, []);

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("subtitle")}</p>
        </div>
        <button onClick={exportCsv} data-testid="export-csv-button" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold">
          <Download className="w-4 h-4" /> {t("exportCsv")}
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Mini label={t("totals.invoices")} value={totals.count ?? 0} />
        <Mini label={t("totals.subtotal")} value={`€${(totals.subtotal ?? 0).toFixed(2)}`} />
        <Mini label={t("totals.vat")} value={`€${(totals.vat ?? 0).toFixed(2)}`} />
        <Mini label={t("totals.total")} value={`€${(totals.total ?? 0).toFixed(2)}`} accent="text-zinc-950" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Mini label={t("totals.paid")} value={`€${(totals.paid ?? 0).toFixed(2)}`} accent="text-emerald-700" />
        <Mini
          label={t("totals.pending")}
          value={`€${((totals.pending ?? 0) + (totals.receivable ?? 0)).toFixed(2)}`}
          accent="text-amber-700"
          warn={(totals.receivable ?? 0) > 0 ? `Inclui €${(totals.receivable ?? 0).toFixed(2)} de eventos concluídos sem recibo emitido.` : null}
        />
      </div>

      {/* View switch */}
      <div className="flex items-center gap-2 flex-wrap">
        {[["invoices", "Recibos"], ["byPromoter", "Por promotor (mês)"]].map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`invoices-view-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${view === k ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-zinc-400" />
        <select data-testid="month-filter" value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
          <option value="">{t("filters.allMonths")}</option>
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {view === "invoices" && (
          <select data-testid="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
            <option value="">{t("filters.allStatus")}</option>
            <option value="pendente">{t("filters.pending")}</option>
            <option value="pago">{t("filters.paid")}</option>
          </select>
        )}
      </div>

      {view === "byPromoter" ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="invoices-by-promoter">
          <div className="px-4 py-2.5 text-xs text-zinc-500 border-b border-zinc-100">Agrupado por <strong>mês do evento</strong>{month ? ` · ${month}` : " · todos os meses"}. Histórico preservado.</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/50 text-xs font-semibold text-zinc-500 uppercase tracking-wider border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4">Promotor</th>
                  <th className="text-right py-3 px-4">Recibos</th>
                  <th className="text-right py-3 px-4">Total a pagar</th>
                  <th className="text-right py-3 px-4">A pagar</th>
                  <th className="text-right py-3 px-4">Pago</th>
                  <th className="text-right py-3 px-4">Pendente</th>
                </tr>
              </thead>
              <tbody>
                {promoterRows.map((r) => (
                  <tr key={r.promoter_id} data-testid={`promoter-finance-row-${r.promoter_id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-4">
                      <Link to={tPath(`/promotores/${r.promoter_id}`)} className="font-medium text-zinc-900 hover:underline">{r.promoter_name || r.promoter_id.slice(0, 6)}</Link>
                    </td>
                    <td className="py-3 px-4 text-right text-zinc-700">{r.count}</td>
                    <td className="py-3 px-4 text-right font-semibold text-zinc-950">€{r.invoiced.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-blue-700">€{(r.receivable || 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-emerald-700">€{r.paid.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-amber-700">€{r.pending.toFixed(2)}</td>
                  </tr>
                ))}
                {promoterRows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-sm text-zinc-500">Sem dados para este período.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-10 text-center text-sm text-zinc-500">{tc("states.loading")}</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/50 text-xs font-semibold text-zinc-500 uppercase tracking-wider border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4">{t("table.number")}</th>
                  <th className="text-left py-3 px-4">{t("table.promoter")}</th>
                  <th className="text-left py-3 px-4 hidden md:table-cell">{t("table.event")}</th>
                  <th className="text-right py-3 px-4 hidden lg:table-cell">{t("table.hours")}</th>
                  <th className="text-right py-3 px-4 hidden lg:table-cell">{t("table.rate")}</th>
                  <th className="text-right py-3 px-4">{t("table.total")}</th>
                  <th className="text-center py-3 px-4">{t("table.status")}</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="py-3 px-4 font-mono text-xs text-zinc-700">{inv.number}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-zinc-900">{inv.promoter_name}</div>
                      {inv.promoter_nif && <div className="text-xs text-zinc-500">NIF {inv.promoter_nif}</div>}
                    </td>
                    <td className="py-3 px-4 text-zinc-700 hidden md:table-cell">
                      <Link to={tPath(`/eventos/${inv.event_id}`)} className="hover:underline">{inv.event_name}</Link>
                      <div className="text-xs text-zinc-500">{inv.event_date}</div>
                    </td>
                    <td className="py-3 px-4 text-right text-zinc-700 hidden lg:table-cell">{inv.hours}</td>
                    <td className="py-3 px-4 text-right text-zinc-700 hidden lg:table-cell">€{inv.hourly_rate}</td>
                    <td className="py-3 px-4 text-right font-semibold text-zinc-950">
                      €{(inv.total - (inv.vat_amount || 0)).toFixed(2)}
                      <div className="text-[10px] text-zinc-500">s/ IVA</div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_CLS[inv.status]}`}>{t(`status.${inv.status}`, inv.status)}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex gap-2 justify-end items-center">
                        <button onClick={() => downloadPdf(inv)} data-testid={`pdf-${inv.id}`} className="text-xs text-zinc-700 hover:text-zinc-950 inline-flex items-center gap-1" title="PDF">
                          <FileDown className="w-3.5 h-3.5" /> {t("actions.pdf")}
                        </button>
                        <button onClick={() => togglePaid(inv)} data-testid={`toggle-paid-${inv.id}`} className="text-xs text-zinc-700 hover:text-zinc-950 inline-flex items-center gap-1">
                          {inv.status === "pago" ? <><RotateCcw className="w-3 h-3" /> {t("actions.reopen")}</> : <><Check className="w-3 h-3" /> {t("actions.markPaid")}</>}
                        </button>
                        <button onClick={() => removeInv(inv)} data-testid={`delete-${inv.id}`} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {data.invoices.length === 0 && (
                  <tr><td colSpan={8} className="py-12 text-center text-sm text-zinc-500">
                    <FileText className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                    {t("empty")}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function Mini({ label, value, accent, warn }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4">
      <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold tracking-tight font-display mt-1 flex items-center gap-1.5 ${accent || "text-zinc-950"}`}>
        {value}
        {warn && (
          <span title={warn} data-testid="pending-receivable-warning" className="inline-flex">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </span>
        )}
      </div>
    </div>
  );
}
